/**
 * ContinuanceMotionPanel — generate the Motion for Continuance and its Order together (Lloyd,
 * Aug 19-25 2026 — Section D, "the motion arrived, and it has traps").
 *
 * Four decisions worth knowing about:
 *
 *  1. **The motion and the order are one action.** A real motion was rejected by a real court for
 *     missing its order, so there is no button that produces one without the other — `handleGenerate`
 *     calls the API once and always gets both documents back, or neither.
 *  2. **Blockers are shown before anything is generated**, refreshed as staff fill in the ground and
 *     the setting date, exactly as the appeal bond panel does — this is a document filed with a
 *     court, and the point at which a wrong or missing fact is cheapest to catch is before it prints.
 *  3. **The two placeholder grounds are visibly flagged.** Only "no local coverage" is Lloyd's own,
 *     confirmed wording; the other two are provisional pending Kelsi's answer, and the picker says so
 *     rather than letting a provisional ground look identical to a settled one.
 *  4. **Send comes after the preview, never instead of it** (18 Sep meeting, items 1–2). It sends
 *     the answers the preview was generated from, not whatever the form holds now, so what reaches
 *     the court is what somebody opened. Whether it emails the court or waits in Drafts is the
 *     `motion.court_autosend` switch, off by default.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  Link as MuiLink,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ContinuanceMotionApi, {
  CONTINUANCE_GROUND_OPTIONS,
  ContinuanceBlockerDto,
  ContinuanceMotionBlockedError,
  ContinuanceMotionResultDto,
  GenerateContinuanceMotionInput,
} from '../../api/ContinuanceMotionApi';
import { MotionSendResultDto } from '../../api/MotionSendResult';
import MotionSendResultAlert from '../MotionSendResultAlert/MotionSendResultAlert';

interface ContinuanceMotionPanelProps {
  Ticket_guid: string;
}

/** A base64 PDF the browser can open without the page having to host it anywhere. */
function pdfObjectUrl(base64: string): string {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
}

const ContinuanceMotionPanel: React.FC<ContinuanceMotionPanelProps> = ({ Ticket_guid }) => {
  const [ground, setGround] = useState('');
  const [settingDate, setSettingDate] = useState('');
  const [servedMethod, setServedMethod] = useState('electronic mail');
  const [servedOn, setServedOn] = useState('the attorney for the State');
  const [showOverrides, setShowOverrides] = useState(false);
  const [courtName, setCourtName] = useState('');
  const [signatureDate, setSignatureDate] = useState('');

  const [blockers, setBlockers] = useState<ContinuanceBlockerDto[]>([]);
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState<ContinuanceMotionResultDto | null>(null);
  const [motionUrl, setMotionUrl] = useState<string | null>(null);
  const [orderUrl, setOrderUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // The answers the current preview was generated from — what Send sends.
  const [previewedInput, setPreviewedInput] = useState<GenerateContinuanceMotionInput | null>(null);
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState<MotionSendResultDto | null>(null);

  const loadReadiness = useCallback(async () => {
    try {
      const dto = await ContinuanceMotionApi.readiness(Ticket_guid, {
        ground,
        settingDate,
        servedMethod,
        servedOn,
        courtName: courtName || undefined,
      });
      setBlockers(dto.blockers);
    } catch {
      // Non-fatal — the generate call re-checks server-side and returns the same blockers, so a
      // failed preview must not stop someone who knows the case is ready.
    }
  }, [Ticket_guid, ground, settingDate, servedMethod, servedOn, courtName]);

  useEffect(() => {
    loadReadiness();
  }, [loadReadiness]);

  // Revoke the previous object URLs when they are replaced or the panel unmounts, or every
  // regenerate leaks a blob for the life of the page.
  useEffect(() => {
    return () => {
      if (motionUrl) URL.revokeObjectURL(motionUrl);
      if (orderUrl) URL.revokeObjectURL(orderUrl);
    };
  }, [motionUrl, orderUrl]);

  const handleGenerate = async () => {
    setGenerating(true);
    setError(null);
    setBlockers([]);
    setResult(null);
    setPreviewedInput(null);
    setSendResult(null);
    try {
      const input: GenerateContinuanceMotionInput = {
        ground,
        settingDate,
        servedMethod,
        servedOn,
        courtName: courtName || null,
        signatureDate: signatureDate || null,
      };
      const dto = await ContinuanceMotionApi.generate(Ticket_guid, input);
      setResult(dto);
      setPreviewedInput(input);
      setMotionUrl(pdfObjectUrl(dto.motion.pdfBase64));
      setOrderUrl(pdfObjectUrl(dto.order.pdfBase64));
      await loadReadiness();
    } catch (err) {
      if (err instanceof ContinuanceMotionBlockedError) {
        setBlockers(
          err.blockers.length > 0 ? err.blockers : [{ field: null, reason: err.message }],
        );
      } else {
        setError(err instanceof Error ? err.message : 'Failed to generate the motion for continuance');
      }
    } finally {
      setGenerating(false);
    }
  };

  const handleSend = async () => {
    if (!previewedInput) return;
    setSending(true);
    setError(null);
    setSendResult(null);
    try {
      setSendResult(await ContinuanceMotionApi.send(Ticket_guid, previewedInput));
    } catch (err) {
      if (err instanceof ContinuanceMotionBlockedError) {
        setBlockers(
          err.blockers.length > 0 ? err.blockers : [{ field: null, reason: err.message }],
        );
      } else {
        setError(err instanceof Error ? err.message : 'The motion was NOT sent.');
      }
    } finally {
      setSending(false);
    }
  };

  const selectedGround = CONTINUANCE_GROUND_OPTIONS.find((option) => option.value === ground);
  const canSubmit =
    ground !== '' &&
    settingDate !== '' &&
    servedMethod.trim() !== '' &&
    servedOn.trim() !== '' &&
    !generating;

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 0.5 }}>
        Motion for continuance
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Generates the motion and the order that must ride with it, captioned for whichever court
        format this case takes. Both documents are produced together, or neither is.
      </Typography>

      {blockers.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>This case is not ready for a motion for continuance</AlertTitle>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {blockers.map((blocker, index) => (
              <li key={blocker.field ?? `blocker-${index}`}>{blocker.reason}</li>
            ))}
          </ul>
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Stack spacing={2} sx={{ mb: 1 }}>
        <FormControl size="small" sx={{ maxWidth: 420 }}>
          <InputLabel id="continuance-ground-label">Ground</InputLabel>
          <Select
            labelId="continuance-ground-label"
            label="Ground"
            value={ground}
            onChange={(e) => setGround(e.target.value)}
          >
            {CONTINUANCE_GROUND_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <span>{option.label}</span>
                  {option.pendingConfirmation && (
                    <Chip size="small" color="warning" label="pending confirmation" />
                  )}
                </Stack>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {selectedGround?.pendingConfirmation && (
          <Alert severity="info" sx={{ maxWidth: 640 }}>
            This ground&rsquo;s wording is provisional, pending confirmation of the firm&rsquo;s
            standard language for it. It will print professionally worded text, but double-check it
            before filing.
          </Alert>
        )}

        <TextField
          label="Setting being continued"
          type="date"
          size="small"
          value={settingDate}
          onChange={(e) => setSettingDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ maxWidth: 220 }}
        />
      </Stack>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 1 }}>
        <TextField
          label="Served on"
          size="small"
          value={servedOn}
          onChange={(e) => setServedOn(e.target.value)}
          helperText="Who the Certificate of Service names"
          sx={{ maxWidth: 300 }}
        />
        <TextField
          label="Served by"
          size="small"
          value={servedMethod}
          onChange={(e) => setServedMethod(e.target.value)}
          helperText="How it is being served"
          sx={{ maxWidth: 220 }}
        />
      </Stack>

      {showOverrides ? (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 1 }}>
          <TextField
            label="Court override"
            size="small"
            value={courtName}
            onChange={(e) => setCourtName(e.target.value)}
            helperText="Only if the case has left the court on file (e.g. after an appeal)"
            sx={{ maxWidth: 320 }}
          />
          <TextField
            label="Signature date"
            type="date"
            size="small"
            value={signatureDate}
            onChange={(e) => setSignatureDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ maxWidth: 220 }}
          />
        </Stack>
      ) : (
        <Button size="small" onClick={() => setShowOverrides(true)} sx={{ mb: 1 }}>
          This is filed in a different court, or on a different day
        </Button>
      )}

      <Box>
        <Button
          variant="contained"
          onClick={handleGenerate}
          disabled={!canSubmit}
          startIcon={generating ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {generating ? 'Generating…' : 'Generate motion and order'}
        </Button>
      </Box>

      {result && (
        <Alert severity="success" sx={{ mt: 2 }}>
          <AlertTitle>Motion and order generated</AlertTitle>
          <Stack spacing={0.5}>
            {motionUrl && (
              <MuiLink href={motionUrl} target="_blank" rel="noopener noreferrer">
                Open {result.motion.fileName}
              </MuiLink>
            )}
            {orderUrl && (
              <MuiLink href={orderUrl} target="_blank" rel="noopener noreferrer">
                Open {result.order.fileName}
              </MuiLink>
            )}
          </Stack>
          {previewedInput && !sendResult && (
            <Box sx={{ mt: 1.5 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={handleSend}
                disabled={sending}
                startIcon={sending ? <CircularProgress size={14} color="inherit" /> : undefined}
              >
                {sending ? 'Sending…' : 'Send both to the court'}
              </Button>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                Sends these two documents exactly as previewed. Unless automatic sending is on in
                Settings, it waits in the court mailbox&rsquo;s Drafts for someone to release.
              </Typography>
            </Box>
          )}
        </Alert>
      )}

      {sendResult && <MotionSendResultAlert result={sendResult} chartStep="MOC" />}

      <Divider sx={{ my: 3 }} />
    </Box>
  );
};

export default ContinuanceMotionPanel;
